/**
 * FOCUSED - Main Theme JavaScript
 * Handles cart drawer, abandoned cart toast, and interactive elements
 */

(function() {
  'use strict';

  // ========================================
  // CART DRAWER FUNCTIONALITY
  // ========================================
  const cartDrawer = {
    drawer: document.getElementById('cart-drawer'),
    overlay: null,
    closeBtn: null,
    cartIcon: document.getElementById('cart-icon'),
    cartCount: document.getElementById('cart-count'),

    init() {
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector('.cart-drawer__overlay');
      this.closeBtn = this.drawer.querySelector('.cart-drawer__close');

      // Event listeners
      if (this.cartIcon) {
        this.cartIcon.addEventListener('click', () => this.open());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
          this.close();
        }
      });

      // Load cart on init
      this.loadCart();
    },

    async open() {
      this.drawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      await this.loadCart();
    },

    close() {
      this.drawer.classList.remove('active');
      document.body.style.overflow = '';
    },

    async loadCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.updateCartUI(cart);
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    },

    updateCartUI(cart) {
      // Update cart count
      if (this.cartCount) {
        this.cartCount.textContent = cart.item_count;
        this.cartCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }

      // Update cart items
      const cartItemsContainer = document.getElementById('cart-items');
      if (!cartItemsContainer) return;

      if (cart.items.length === 0) {
        cartItemsContainer.innerHTML = '<p style="text-align: center; color: var(--text-secondary); padding: 2rem;">Your cart is empty</p>';
        return;
      }

      cartItemsContainer.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-line="${item.line}">
          <img src="${item.image}" alt="${item.title}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;">
          <div style="flex: 1; margin-left: 1rem;">
            <div style="font-weight: 600; margin-bottom: 0.25rem;">${item.product_title}</div>
            <div style="font-size: 0.875rem; color: var(--text-secondary);">${item.variant_title || ''}</div>
            <div style="font-family: var(--font-mono); color: var(--gold); margin-top: 0.5rem;">${this.formatMoney(item.price)}</div>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <button class="cart-item-qty-btn" data-line="${item.line}" data-action="decrease">−</button>
            <span style="min-width: 30px; text-align: center;">${item.quantity}</span>
            <button class="cart-item-qty-btn" data-line="${item.line}" data-action="increase">+</button>
          </div>
        </div>
      `).join('');

      // Update subtotal
      const subtotalEl = document.getElementById('cart-subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(cart.total_price);
      }

      // Add quantity button listeners
      document.querySelectorAll('.cart-item-qty-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const line = e.target.dataset.line;
          const action = e.target.dataset.action;
          const currentItem = cart.items.find(item => item.line == line);

          if (currentItem) {
            const newQty = action === 'increase' ? currentItem.quantity + 1 : currentItem.quantity - 1;
            this.updateQuantity(line, newQty);
          }
        });
      });
    },

    async updateQuantity(line, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        const cart = await response.json();
        this.updateCartUI(cart);
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    },

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // Initialize cart drawer
  cartDrawer.init();

  // ========================================
  // ABANDONED CART TOAST
  // ========================================
  const abandonedCartToast = {
    toast: document.getElementById('abandoned-cart-toast'),
    delay: 18000, // 18 seconds
    timeout: null,
    hasShown: false,

    init() {
      if (!this.toast) return;

      // Show toast when items are in cart after delay
      this.checkCart();

      // Close button
      const closeBtn = this.toast.querySelector('.abandoned-cart-toast__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.hide());
      }

      // Listen for cart changes
      document.addEventListener('cart:updated', () => {
        this.checkCart();
      });
    },

    async checkCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        if (cart.item_count > 0 && !this.hasShown) {
          this.scheduleShow();
        }
      } catch (error) {
        console.error('Error checking cart:', error);
      }
    },

    scheduleShow() {
      if (this.timeout) clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        this.show();
      }, this.delay);
    },

    show() {
      this.toast.classList.remove('hidden');
      this.hasShown = true;
    },

    hide() {
      this.toast.classList.add('hidden');
    }
  };

  // Initialize abandoned cart toast
  abandonedCartToast.init();

  // ========================================
  // ADD TO CART FUNCTIONALITY
  // ========================================
  document.addEventListener('click', async function(e) {
    if (e.target.matches('[data-add-to-cart]') || e.target.closest('[data-add-to-cart]')) {
      e.preventDefault();
      const btn = e.target.matches('[data-add-to-cart]') ? e.target : e.target.closest('[data-add-to-cart]');
      const variantId = btn.dataset.variantId;

      if (!variantId) return;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (response.ok) {
          const item = await response.json();

          // Track with Klaviyo
          if (typeof _learnq !== 'undefined') {
            _learnq.push(['track', 'Added to Cart', {
              '$value': item.price / 100,
              'ProductName': item.product_title,
              'ProductID': item.product_id,
              'SKU': item.sku,
              'Quantity': item.quantity
            }]);
          }

          // Track with Meta Pixel
          if (typeof fbq !== 'undefined') {
            fbq('track', 'AddToCart', {
              content_name: item.product_title,
              content_ids: [item.product_id],
              content_type: 'product',
              value: item.price / 100,
              currency: 'USD'
            });
          }

          // Track with TikTok Pixel
          if (typeof ttq !== 'undefined') {
            ttq.track('AddToCart', {
              content_name: item.product_title,
              content_id: item.product_id,
              content_type: 'product',
              value: item.price / 100,
              currency: 'USD'
            });
          }

          // Open cart drawer
          cartDrawer.open();

          // Dispatch custom event
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }
  });

  // ========================================
  // SMOOTH SCROLL FOR ANCHOR LINKS
  // ========================================
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // ========================================
  // SCROLL ANIMATIONS (Fade In)
  // ========================================
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('fade-in');
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observe sections for fade-in animation
  document.querySelectorAll('.section').forEach(section => {
    observer.observe(section);
  });

  // ========================================
  // NUMBER COUNTER ANIMATION (Social Proof)
  // ========================================
  function animateCounter(element, target) {
    const duration = 2000;
    const start = 0;
    const increment = target / (duration / 16);
    let current = start;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        element.textContent = target;
        clearInterval(timer);
      } else {
        element.textContent = Math.floor(current).toLocaleString();
      }
    }, 16);
  }

  // Animate social proof numbers when in view
  const socialProofObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const number = entry.target.textContent.replace(/[^0-9]/g, '');
        if (number && !isNaN(number)) {
          animateCounter(entry.target, parseInt(number));
        }
        socialProofObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  document.querySelectorAll('.social-proof__number').forEach(el => {
    const hasNumber = /\d/.test(el.textContent);
    if (hasNumber && !el.textContent.includes('★')) {
      socialProofObserver.observe(el);
    }
  });

})();

// ========================================
// GLOBAL UTILITY FUNCTIONS
// ========================================
window.FocusedTheme = {
  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
};
